/**
 * First-touch attribution — docs/09-instrumentation.md
 *
 * This is the one decision in the build that cannot be reconstructed later.
 * The headline metric is organic revenue. In month nine somebody asks how much
 * of that was actually organic versus paid versus direct, and if the answer is
 * vague the case study is dead.
 *
 * The problem it solves: a carrier reads a state page in March and buys in May
 * via a direct visit. Naive attribution calls that direct. It was organic.
 *
 * Rules:
 *   - Capture on FIRST visit only. Refreshed on each visit, never overwritten
 *     on source. First touch wins, permanently, for 90 days.
 *   - Landing page is captured too. Which state page or collection started the
 *     session is the whole story of this case study.
 *   - Written to a first-party cookie so it survives ITP better than
 *     localStorage, and so the checkout can read it.
 *
 * Loaded from the head, deferred. It has to run on the first page of the first
 * session, and defer executes after parse but before load — comfortably before
 * a visitor can navigate away. Blocking the parser would spend the LCP budget
 * in docs/07-technical-seo.md on a ~1KB cookie write.
 */
(function () {
  "use strict";

  var COOKIE = "mmf_first_touch";
  var DAYS = 90;

  function readCookie(name) {
    var match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function writeCookie(name, value, days) {
    var expires = new Date(Date.now() + days * 864e5).toUTCString();
    // SameSite=Lax so it survives the return trip from an external referrer.
    document.cookie =
      name + "=" + encodeURIComponent(value) +
      "; expires=" + expires +
      "; path=/; SameSite=Lax" +
      (location.protocol === "https:" ? "; Secure" : "");
  }

  function classify(params, referrer) {
    // Explicit UTM wins. It is the only source we did not have to infer.
    if (params.get("utm_source")) {
      return {
        source: params.get("utm_source"),
        medium: params.get("utm_medium") || "(not set)",
        campaign: params.get("utm_campaign") || "(not set)"
      };
    }
    if (params.get("gclid")) {
      return { source: "google", medium: "cpc", campaign: "(gclid)" };
    }
    if (!referrer) {
      return { source: "(direct)", medium: "(none)", campaign: "(not set)" };
    }

    var host;
    try {
      host = new URL(referrer).hostname.replace(/^www\./, "");
    } catch (e) {
      return { source: "(unknown)", medium: "referral", campaign: "(not set)" };
    }

    if (host === location.hostname.replace(/^www\./, "")) return null; // internal

    // Deliberately a small, honest list rather than a 400-entry engine table.
    // Anything unrecognised is reported as a referral, not guessed at.
    var engines = /^(google|bing|duckduckgo|yahoo|ecosia|brave|search\.marginalia)\./;
    var ai = /^(chatgpt\.com|chat\.openai\.com|perplexity\.ai|claude\.ai|copilot\.microsoft\.com|gemini\.google\.com)$/;

    if (ai.test(host)) return { source: host, medium: "ai_referral", campaign: "(not set)" };
    if (engines.test(host + ".")) return { source: host, medium: "organic", campaign: "(not set)" };
    return { source: host, medium: "referral", campaign: "(not set)" };
  }

  var existing = readCookie(COOKIE);

  if (existing) {
    // Refresh the expiry so an engaged visitor does not age out mid-consideration.
    // The VALUE is never touched. That is the whole point of first touch.
    writeCookie(COOKIE, existing, DAYS);
    return;
  }

  var attribution = classify(new URLSearchParams(location.search), document.referrer);
  if (!attribution) return; // internal referrer on a first hit — nothing to record

  attribution.landing_page = location.pathname;
  attribution.first_seen = new Date().toISOString();

  writeCookie(COOKIE, JSON.stringify(attribution), DAYS);

  // Expose for the checkout hand-off. scripts/ and the Customer Events pixel
  // read this to write first touch onto the order. See docs/09-instrumentation.md.
  window.ffFirstTouch = attribution;
})();
